/**
 *
 * Datadog Browser Logs SDK for collecting and forwarding browser logs to Datadog.
 * Provides comprehensive logging capabilities with automatic error tracking and custom log collection.
 *
 * @packageDocumentation
 * @see [Browser Log Collection](https://docs.datadoghq.com/logs/log_collection/javascript/)
 */

import { globalObject } from '@datadog/js-core/util'
import { defineGlobal } from '@datadog/browser-core'
import type { LogsPublicApi } from '../boot/logsPublicApi'
import { makeLogsPublicApi } from '../boot/logsPublicApi'

export type { InternalContext } from '../domain/contexts/internalContext'
export type { LogsMessage } from '../domain/logger'
export { Logger, HandlerType } from '../domain/logger'
export { StatusType } from '../domain/logger/isAuthorized'
export type { LoggerConfiguration, LogsPublicApi as DatadogLogs } from '../boot/logsPublicApi'
export type { LogsInitConfiguration, LogsBeforeSend } from '../domain/configuration'
export type { LogsEvent } from '../logsEvent.types'
export type { LogsPlugin, OnLogsStartOptions, OnInitOptions } from '../domain/plugins'
export type {
  LogsEventDomainContext,
  NetworkLogsEventDomainContext,
  ConsoleLogsEventDomainContext,
  LoggerLogsEventDomainContext,
  ReportLogsEventDomainContext,
} from '../domainContext.types'

/**
 * @deprecated Use {@link DatadogLogs} instead
 */
export type LogsGlobal = LogsPublicApi

export type {
  InitConfiguration,
  PublicApi,
  User,
  Account,
  TraceContextInjection,
  SessionPersistence,
  TrackingConsent,
  MatchOption,
  RawReportType,
  ErrorSource,
} from '@datadog/browser-core'
export type { Context, ContextValue, ContextArray } from '@datadog/js-core/util'
export type { ProxyFn, Site } from '@datadog/js-core/transport'
export type { ConsoleApiName } from '@datadog/js-core/util'

/**
 * The global Logs instance. Use this to call Logs methods.
 *
 * @category Main
 * @see {@link DatadogLogs}
 * @see [Browser Log Collection](https://docs.datadoghq.com/logs/log_collection/javascript/)
 */
export const datadogLogs = makeLogsPublicApi({ sdkName: 'logs' })

interface BrowserWindow {
  DD_LOGS?: LogsPublicApi
}
defineGlobal(globalObject as BrowserWindow, 'DD_LOGS', datadogLogs)
